import { BadgeCheck, Star, Store as StoreIcon } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import { cn } from '@/lib/utils';

import { StoreImage, storeLogoUrl } from '../storeMedia';
import { StoreRowMenu, type RequestStoreAction } from './StoreQuickActions';
import { StoreStatusBadge } from './StoreStatusBadge';
import { formatDate, formatGmv, ownerLabel, planSummary, statusReason, storefrontPath } from './segments';

const th = 'px-4 py-2.5 text-left text-xs font-medium text-gray-500';
const td = 'px-4 py-3 align-middle';
const num = 'text-right tabular-nums';

/** Store roster as a dense table — the same rows as the cards, comparable at a glance. */
export const StoresTable: React.FC<{
  rows: AdminStore[];
  loading: boolean;
  onOpen: (id: number) => void;
  onAction: RequestStoreAction;
}> = ({ rows, loading, onOpen, onAction }) => {
  if (!loading && rows.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-gray-50/80 p-12 text-center text-gray-500">
        <StoreIcon className="mx-auto mb-3 h-12 w-12 text-gray-300" />
        <p className="text-sm font-medium">No stores match these filters</p>
        <p className="mt-1 text-xs">Adjust the search or clear a filter above.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full min-w-[980px] text-sm">
        <thead className="border-b border-gray-200 bg-gray-50/70">
          <tr>
            <th className={th}>Store</th>
            <th className={th}>Owner</th>
            <th className={th}>Status</th>
            <th className={th}>Plan</th>
            <th className={cn(th, 'text-right')}>Listings</th>
            <th className={cn(th, 'text-right')}>Orders</th>
            <th className={cn(th, 'text-right')}>GMV</th>
            <th className={th}>Created</th>
            <th className={th}><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading
            ? [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <tr key={i} aria-hidden="true">
                <td className={td} colSpan={9}><div className="h-10 animate-pulse rounded bg-gray-100" /></td>
              </tr>
            ))
            : rows.map((store) => {
              const plan = planSummary(store);
              const reason = statusReason(store);
              return (
                <tr
                  key={store.storeid}
                  onClick={() => onOpen(store.storeid)}
                  className="cursor-pointer hover:bg-blue-50/40"
                >
                  <td className={td}>
                    <div className="flex items-center gap-3">
                      <StoreImage
                        src={storeLogoUrl(store)}
                        alt=""
                        className="h-10 w-10 shrink-0 rounded-lg border border-gray-200 bg-gray-50"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          {/* The row is clickable for the mouse; this is the keyboard path. */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onOpen(store.storeid); }}
                            className="max-w-[16rem] truncate font-semibold text-gray-900 hover:underline focus-visible:underline focus-visible:outline-none"
                          >
                            {store.name}
                          </button>
                          {store.verified === 1 && <BadgeCheck className="h-4 w-4 shrink-0 text-blue-500" aria-label="Verified" />}
                          {store.featured === 1 && <Star className="h-4 w-4 shrink-0 text-purple-500" aria-label="Featured" />}
                        </div>
                        <p className="max-w-[18rem] truncate text-xs text-gray-400">
                          {storefrontPath(store) ?? 'No handle'} · #{store.storeid}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className={td}>
                    <p className="max-w-[12rem] truncate text-gray-900">{ownerLabel(store)}</p>
                    <p className="max-w-[12rem] truncate text-xs text-gray-400">
                      {store.oid != null ? 'Organization' : store.owner?.email ?? 'Individual'}
                    </p>
                  </td>
                  <td className={td}>
                    <div className="flex flex-wrap items-center gap-1">
                      <StoreStatusBadge store={store} />
                      {!!store.payout_hold && (
                        <span className="inline-flex rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold leading-5 text-orange-800">Payouts held</span>
                      )}
                    </div>
                    {reason && <p className="mt-0.5 max-w-[14rem] truncate text-xs text-gray-400" title={reason}>{reason}</p>}
                  </td>
                  <td className={td}>
                    <p className="text-gray-900">{plan.label}</p>
                    {plan.detail && <p className={cn('text-xs', plan.expired ? 'text-red-600' : 'text-gray-400')}>{plan.detail}</p>}
                  </td>
                  <td className={cn(td, num)}>
                    <span className="font-medium text-gray-900">{store.active_listing_count ?? '—'}</span>
                    <span className="text-gray-400"> / {store.listing_count ?? '—'}</span>
                  </td>
                  <td className={cn(td, num, 'font-medium text-gray-900')}>{store.order_count ?? '—'}</td>
                  <td className={cn(td, num, 'font-medium text-gray-900')}>{formatGmv(store.gmv)}</td>
                  <td className={cn(td, 'whitespace-nowrap text-gray-500')}>{formatDate(store.date_added)}</td>
                  <td className={cn(td, 'text-right')}>
                    <StoreRowMenu store={store} onAction={onAction} />
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
};
